import React from "react";
import { Wallet, FileSignature, TrendingUp, Receipt, PiggyBank, Info } from "lucide-react";
import { Panel, TitledPanel, EmptyState, IconTile } from "@/portal/ui/Layout";
import { StatusBadge, COST_STATUS } from "@/portal/ui/Badge";
import { fmtDateHuman } from "@/portal/format";

const KPIS = [
  ["contract_value", "Valoare contract", FileSignature, ""],
  ["approved_changes", "Modificări aprobate", TrendingUp, "amber"],
  ["forecast_final", "Estimare cost final", Wallet, ""],
  ["invoiced", "Facturat până acum", Receipt, "neutral"],
  ["paid", "Plătit până acum", PiggyBank, "success"],
];

export default function BudgetTab({ portal }) {
  const b = portal?.budget || {};
  const costs = portal?.costs || [];
  const hasBudget = KPIS.some(([k]) => b[k]);
  if (!hasBudget && costs.length === 0) {
    return <EmptyState icon={Wallet} title="Informațiile de buget nu au fost încă publicate" description="Echipa Brisk va publica aici valoarea contractului, modificările aprobate și situația facturării." testid="budget-empty" />;
  }
  const byStatus = costs.reduce((acc, c) => { acc[c.status] = (acc[c.status] || 0) + 1; return acc; }, {});

  return (
    <div className="space-y-5">
      {hasBudget && (
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4" data-testid="budget-kpis">
          {KPIS.filter(([k]) => b[k]).map(([k, label, Icon, tone]) => (
            <div key={k} className="cp-panel px-4 py-4 flex items-center gap-3.5 min-w-0" data-testid={`budget-${k}`}>
              <IconTile icon={Icon} tone={tone} />
              <div className="min-w-0 pl-3.5 border-l border-cp-border"><div className="text-[12.5px] text-cp-text-2">{label}</div><div className="cp-serif text-[20px] text-cp-text mt-0.5 truncate">{b[k]}</div></div>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-5 items-start">
        <div>
          <div className="flex items-baseline justify-between gap-3 mb-3">
            <h2 className="cp-h3">Situația costurilor</h2>
            {costs.length > 0 && <span className="cp-meta">{costs.length} {costs.length === 1 ? "poziție" : "poziții"}</span>}
          </div>
          {costs.length === 0 ? <EmptyState compact icon={Receipt} title="Nicio poziție de cost publicată" /> : (
            <Panel padded={false} className="overflow-x-auto" data-testid="costs-table">
              <table className="cp-table min-w-[640px]">
                <thead><tr><th>Poziție</th><th>Categorie</th><th>Dată</th><th className="text-right">Valoare</th><th>Status</th></tr></thead>
                <tbody>
                  {costs.map((c) => (
                    <tr key={c.id} data-testid={`cost-${c.id}`}>
                      <td><div className="text-[14px] font-medium text-cp-text">{c.title}</div>{c.note && <div className="cp-meta mt-0.5">{c.note}</div>}</td>
                      <td>{c.category || "—"}</td>
                      <td className="whitespace-nowrap">{fmtDateHuman(c.date)}</td>
                      <td className="text-right font-semibold text-cp-text tabular-nums whitespace-nowrap">{c.amount || "—"}</td>
                      <td><StatusBadge map={COST_STATUS} value={c.status} fallback="planned" size="sm" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Panel>
          )}
        </div>
        <div className="space-y-4">
          {(b.contingency || b.currency) && (
            <TitledPanel title="Rezerve & monedă" testid="budget-side">
              <dl className="space-y-3">
                {b.contingency && <div><dt className="cp-meta">Rezervă de contingență</dt><dd className="text-[15px] font-semibold text-cp-text mt-0.5">{b.contingency}</dd></div>}
                {b.currency && <div><dt className="cp-meta">Monedă de raportare</dt><dd className="text-[15px] font-semibold text-cp-text mt-0.5">{b.currency}</dd></div>}
              </dl>
            </TitledPanel>
          )}
          {costs.length > 0 && (
            <TitledPanel title="Sumar poziții">
              <ul className="space-y-2">
                {Object.entries(COST_STATUS).filter(([k]) => byStatus[k]).map(([k, s]) => (
                  <li key={k} className="flex items-center justify-between text-[13.5px]"><span className="text-cp-text-2">{s.label}</span><span className="font-semibold tabular-nums">{byStatus[k]}</span></li>
                ))}
              </ul>
            </TitledPanel>
          )}
          <div className="cp-panel-soft p-4 flex gap-3 text-[13px] text-cp-text-2" data-testid="budget-note">
            <Info className="h-4 w-4 text-cp-accent shrink-0 mt-0.5" aria-hidden="true" />
            <p>{b.note || "Valorile sunt publicate de echipa Brisk pe baza contractului și a modificărilor aprobate. Pentru detalii, contactează managerul de proiect."}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
